import { useState, useCallback } from "react";
import * as XLSX from "xlsx";

const useExcelImport = ({ templateInputs, SetNumbers }) => {
  const [importedData, setImportedData] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [disableUpload, setDisableUpload] = useState(true);
  const [uploadError, setUploadError] = useState("");

  const uploadProps = {
    onRemove: () => {
      setImportedData([]);
      setFileList([]);
      setDisableUpload(true);
      setUploadError("");
      SetNumbers([]);
    },
    beforeUpload: (file) => {
      setImportedData([]);
      setUploadError("");
      const isExcel =
        file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!isExcel) {
        setDisableUpload(true);
        setUploadError(
          "Only .xlsx files are accepted. Please select a valid Excel file."
        );
        return false;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const binaryString = event.target.result;
        const workbook = XLSX.read(binaryString, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length > 10000) {
          setDisableUpload(true);
          setUploadError(
            `File has ${jsonData.length.toLocaleString()} rows. Maximum allowed is 10,000 rows.`
          );
          setImportedData([]);
          setFileList([]);
          SetNumbers([]);
          return;
        }

        const dataLength = jsonData[0]?.length || 0;
        const expectedLength = templateInputs.length + 1;

        if (dataLength !== expectedLength) {
          setDisableUpload(true);
          setUploadError(
            `Wrong number of columns. Expected ${expectedLength} (1 mobile number + ${
              templateInputs.length
            } variable${
              templateInputs.length !== 1 ? "s" : ""
            }), but got ${dataLength}.`
          );
          setImportedData([]);
          setFileList([]);
          SetNumbers([]);
          return;
        }

        setUploadError("");
        setDisableUpload(true);
        setFileList([file]);

        const numbersArray = jsonData?.map((item) => item[0]);
        SetNumbers(numbersArray);
        const updatedMobileNumbers = jsonData.map((item) => ({
          number: item[0],
          value: item.length > 1 ? item.slice(1) : [],
        }));
        setImportedData(updatedMobileNumbers);
      };

      reader.readAsBinaryString(file);
      return false; // prevent auto-upload
    },
  };

  const resetExcel = useCallback(() => {
    setImportedData([]);
    setFileList([]);
    setDisableUpload(true);
    setUploadError("");
  }, []);

  const downloadSampleExcel = () => {
    const numVariables = templateInputs.length;

    // Two example rows, no header row (per guidelines: data only)
    const rows = [
      [
        "919876543210",
        ...Array.from(
          { length: numVariables },
          (_, i) => `SampleValue${i + 1}`
        ),
      ],
      [
        "919876543211",
        ...Array.from(
          { length: numVariables },
          (_, i) => `SampleValue${i + 1}`
        ),
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Campaign");
    XLSX.writeFile(wb, "sample_campaign.xlsx");
  };

  return {
    importedData,
    fileList,
    disableUpload,
    uploadError,
    uploadProps,
    resetExcel,
    downloadSampleExcel,
    setImportedData,
  };
};

export default useExcelImport;
